// Compute totals for Stripe-style tiered prices. Used by both the quote
// builder (frontend) and the contract/proposal pipeline (server) so the
// same line shows the same number everywhere.
//
// Stripe stores tiers as `{ up_to, unit_amount, flat_amount, ... }` with
// `unit_amount = null` on the parent Price (the per-unit price varies by
// quantity). The previous code path treated the first tier's `flat_amount`
// as a per-seat price and multiplied by qty, so a "99 EUR floor + 29 EUR
// per extra seat" plan was billed 99 × seats instead of 99 + 29 × (seats - 3).

export type RawTier = Record<string, unknown>;

function readNumber(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string" && v !== "") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function tierUnitCents(t: RawTier): number {
  return readNumber(t.unit_amount) ?? readNumber(t.unit_amount_decimal) ?? 0;
}

function tierFlatCents(t: RawTier): number {
  return readNumber(t.flat_amount) ?? readNumber(t.flat_amount_decimal) ?? 0;
}

function tierUpTo(t: RawTier): number {
  // Stripe sets up_to=null on the last (uncapped) tier.
  return readNumber(t.up_to) ?? Number.POSITIVE_INFINITY;
}

export function computeTieredCents(
  tiersMode: string | null | undefined,
  rawTiers: RawTier[] | null | undefined,
  qty: number
): number | null {
  if (!rawTiers || rawTiers.length === 0) return null;
  if (qty <= 0) return 0;
  const tiers = rawTiers.slice().sort((a, b) => tierUpTo(a) - tierUpTo(b));

  if (tiersMode === "volume") {
    const tier = tiers.find((t) => qty <= tierUpTo(t)) ?? tiers[tiers.length - 1];
    return tierFlatCents(tier) + tierUnitCents(tier) * qty;
  }

  if (tiersMode === "graduated") {
    let total = 0;
    let remaining = qty;
    let prevCap = 0;
    for (const tier of tiers) {
      if (remaining <= 0) break;
      const cap = tierUpTo(tier);
      const slot = cap - prevCap;
      const used = Math.min(remaining, slot);
      total += tierFlatCents(tier) + tierUnitCents(tier) * used;
      remaining -= used;
      prevCap = cap;
    }
    return total;
  }

  return null;
}

// A "flat fee" tiered price charges a fixed amount regardless of quantity:
// every tier carries only a flat_amount and no per-unit component. The
// business seat plans are modelled this way (e.g. a single uncapped tier of
// €149/mo), so the line total is independent of the seat count and the quote
// should show an amount of 1 (per account) rather than per seat.
export function priceTiersAreFlatFee(
  tiersMode: string | null | undefined,
  rawTiers: RawTier[] | null | undefined
): boolean {
  if (!rawTiers || rawTiers.length === 0) return false;
  return rawTiers.every((t) => tierUnitCents(t) === 0);
}

export type LineLike = {
  unit_amount: number;
  tiers_mode?: string | null;
  tiers?: RawTier[] | null;
  discount_cents?: number;
};

export function computeLineTotalCents(line: LineLike, qty: number): number {
  // A discount overrides any tiered pricing: the discounted unit_amount
  // becomes a flat per-unit rate so the customer sees the discount applied
  // instead of the original tier formula. We anchor on `unit_amount ===
  // discount_cents` so callers that override unit_amount (e.g. computing the
  // original strikethrough total) fall back to tiered/flat logic.
  const discount = line.discount_cents;
  if (discount != null && discount > 0 && line.unit_amount === discount) {
    return discount * qty;
  }
  const tiered = computeTieredCents(line.tiers_mode, line.tiers, qty);
  if (tiered != null) return tiered;
  return line.unit_amount * qty;
}
